import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldWarning, EnvelopeSimple, ChatCircle, Code, ArrowSquareOut, FloppyDisk,
} from "@phosphor-icons/react";

const FIELDS = [
  { key: "RESEND_API_KEY", label: "Resend API Key", placeholder: "re_…", section: "email", secret: true },
  { key: "SENDER_EMAIL", label: "Sender Email", placeholder: "noreply@your-domain.com", section: "email", secret: false },
  { key: "TWILIO_ACCOUNT_SID", label: "Twilio Account SID", placeholder: "AC…", section: "sms", secret: true },
  { key: "TWILIO_AUTH_TOKEN", label: "Twilio Auth Token", placeholder: "…", section: "sms", secret: true },
  { key: "TWILIO_FROM_NUMBER", label: "Twilio From Number", placeholder: "+15551234567", section: "sms", secret: false },
  { key: "JUDGE0_URL", label: "Judge0 URL", placeholder: "https://judge0.your-host.com", section: "code", secret: false },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [configured, setConfigured] = useState({});
  const [edits, setEdits] = useState({});
  const [judge0, setJudge0] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testTarget, setTestTarget] = useState({ email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, j, l] = await Promise.all([
      api.get("/settings"),
      api.get("/judge0/status"),
      api.get("/notify/log"),
    ]);
    setSettings(s.data.settings);
    setConfigured(s.data.configured);
    setJudge0(j.data);
    setLogs(l.data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", edits);
      toast.success("Settings saved");
      setEdits({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const testEmail = async () => {
    if (!testTarget.email) { toast.error("Enter test email"); return; }
    try {
      const r = await api.post("/notify/test", { channel: "email", to: testTarget.email });
      if (r.data.ok) toast.success("Test email sent");
      else toast.error(r.data.error || "Email send failed");
      load();
    } catch { toast.error("Failed"); }
  };

  const testSms = async () => {
    if (!testTarget.phone) { toast.error("Enter test phone (E.164)"); return; }
    try {
      const r = await api.post("/notify/test", { channel: "sms", to: testTarget.phone });
      if (r.data.ok) toast.success("Test SMS sent");
      else toast.error(r.data.error || "SMS send failed");
      load();
    } catch { toast.error("Failed"); }
  };

  const Section = ({ icon: Icon, title, desc, children, status }) => (
    <div className="border border-zinc-200 mb-6">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
        <div className="flex items-center gap-3">
          <Icon size={22} weight="regular" className="text-zinc-900" />
          <div>
            <div className="font-display text-lg font-bold">{title}</div>
            <div className="text-xs text-zinc-500">{desc}</div>
          </div>
        </div>
        {status}
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );

  const fieldsBySection = (section) => FIELDS.filter((f) => f.section === section);

  return (
    <div>
      <PageHeader
        overline="Configuration"
        title="Admin Settings"
        subtitle="Configure integrations, manage notification keys, and monitor sandbox status."
        action={
          <a
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/docs`}
            target="_blank"
            rel="noreferrer"
            className="border border-zinc-300 px-4 py-2.5 text-sm font-medium hover:bg-zinc-50 inline-flex items-center gap-2"
            data-testid="api-docs-link"
          >
            <Code size={14} /> API Docs <ArrowSquareOut size={12} />
          </a>
        }
      />

      <Section
        icon={Code}
        title="Code Execution Sandbox"
        desc="Where coding question test runs are executed."
        status={
          judge0?.configured && judge0?.ok ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold border border-green-600 bg-green-50 text-green-700 px-2 py-1" data-testid="judge0-status">
              <ShieldCheck size={12} weight="fill" /> Judge0 connected
            </span>
          ) : judge0?.configured ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold border border-amber-600 bg-amber-50 text-amber-700 px-2 py-1" data-testid="judge0-status">
              <ShieldWarning size={12} weight="fill" /> Judge0 unreachable
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold border border-red-600 bg-red-50 text-red-700 px-2 py-1" data-testid="judge0-status">
              <ShieldWarning size={12} weight="fill" /> Local subprocess — UNSAFE for prod
            </span>
          )
        }
      >
        {!judge0?.configured && (
          <div className="border-l-4 border-amber-600 bg-amber-50 p-4 text-sm text-amber-900">
            Set <code className="font-mono">JUDGE0_URL</code> to your self-hosted Judge0 instance (e.g. <code>https://judge0.your-domain.com</code>). Until then,
            untrusted code from coding questions is executed via a local subprocess on the backend container — fine for trusted demos, but <strong>do not enable coding questions in production</strong> without Judge0.
          </div>
        )}
        {fieldsBySection("code").map((f) => (
          <SettingField key={f.key} f={f} value={edits[f.key] ?? ""} placeholder={settings[f.key] || f.placeholder} onChange={(v) => setEdits({ ...edits, [f.key]: v })} configured={configured[f.key]} />
        ))}

        <label className="flex items-start gap-3 cursor-pointer p-3 border border-zinc-200 mt-2" data-testid="coding-gate-toggle-row">
          <input
            type="checkbox"
            checked={(edits.DISABLE_CODING_NO_JUDGE0 ?? (configured.DISABLE_CODING_NO_JUDGE0 ? "true" : "")) === "true"}
            onChange={(e) => setEdits({ ...edits, DISABLE_CODING_NO_JUDGE0: e.target.checked ? "true" : "" })}
            className="mt-1 w-4 h-4"
            data-testid="coding-gate-toggle"
          />
          <div>
            <div className="font-medium text-sm">Block coding questions when Judge0 is unavailable</div>
            <div className="text-xs text-zinc-500">When ON, examiners can't create coding questions and students can't start exams containing them unless Judge0 is connected. Recommended for production.</div>
          </div>
        </label>
      </Section>

      <Section
        icon={EnvelopeSimple}
        title="Email Notifications (Resend)"
        desc="Used for welcome, result, and certificate emails."
        status={
          configured.RESEND_API_KEY ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold border border-green-600 bg-green-50 text-green-700 px-2 py-1">
              <ShieldCheck size={12} weight="fill" /> Configured
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold border border-zinc-300 text-zinc-600 px-2 py-1">
              Not configured
            </span>
          )
        }
      >
        {fieldsBySection("email").map((f) => (
          <SettingField key={f.key} f={f} value={edits[f.key] ?? ""} placeholder={settings[f.key] || f.placeholder} onChange={(v) => setEdits({ ...edits, [f.key]: v })} configured={configured[f.key]} />
        ))}
        <div className="flex gap-2 pt-2 border-t border-zinc-200">
          <input
            type="email"
            placeholder="you@example.com"
            value={testTarget.email}
            onChange={(e) => setTestTarget({ ...testTarget, email: e.target.value })}
            className="flex-1 border border-zinc-300 px-3 py-2 text-sm"
            data-testid="test-email-input"
          />
          <button
            onClick={testEmail}
            className="border border-zinc-300 px-4 py-2 text-sm font-medium hover:bg-zinc-50"
            data-testid="send-test-email-btn"
          >
            Send test email
          </button>
        </div>
      </Section>

      <Section
        icon={ChatCircle}
        title="SMS Notifications (Twilio)"
        desc="Used for exam reminders and OTP messages."
        status={
          configured.TWILIO_ACCOUNT_SID && configured.TWILIO_AUTH_TOKEN && configured.TWILIO_FROM_NUMBER ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold border border-green-600 bg-green-50 text-green-700 px-2 py-1">
              <ShieldCheck size={12} weight="fill" /> Configured
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold border border-zinc-300 text-zinc-600 px-2 py-1">
              Not configured
            </span>
          )
        }
      >
        {fieldsBySection("sms").map((f) => (
          <SettingField key={f.key} f={f} value={edits[f.key] ?? ""} placeholder={settings[f.key] || f.placeholder} onChange={(v) => setEdits({ ...edits, [f.key]: v })} configured={configured[f.key]} />
        ))}
        <div className="flex gap-2 pt-2 border-t border-zinc-200">
          <input
            type="tel"
            placeholder="+15551234567"
            value={testTarget.phone}
            onChange={(e) => setTestTarget({ ...testTarget, phone: e.target.value })}
            className="flex-1 border border-zinc-300 px-3 py-2 text-sm"
            data-testid="test-sms-input"
          />
          <button
            onClick={testSms}
            className="border border-zinc-300 px-4 py-2 text-sm font-medium hover:bg-zinc-50"
            data-testid="send-test-sms-btn"
          >
            Send test SMS
          </button>
        </div>
      </Section>

      <button
        onClick={save}
        disabled={busy || Object.keys(edits).length === 0}
        className="bg-zinc-900 text-white px-6 py-3 text-sm font-medium hover:bg-zinc-800 inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="save-settings-btn"
      >
        <FloppyDisk size={14} /> {busy ? "Saving…" : `Save changes${Object.keys(edits).length ? ` (${Object.keys(edits).length})` : ""}`}
      </button>

      <div className="mt-12">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Notification log</div>
        {logs.length === 0 ? (
          <div className="border border-zinc-200 p-8 text-center text-sm text-zinc-500">No notifications sent yet.</div>
        ) : (
          <div className="border border-zinc-200" data-testid="notif-log">
            {logs.slice(0, 20).map((l) => (
              <div key={l.log_id} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 last:border-b-0 text-sm items-center">
                <div className="col-span-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{l.channel}</div>
                <div className="col-span-5 truncate">{l.subject || l.body}</div>
                <div className="col-span-3 text-zinc-500 font-mono text-xs">{l.to}</div>
                <div className="col-span-2 text-zinc-500 text-xs">{new Date(l.created_at).toLocaleString()}</div>
                <div className="col-span-1 text-right">
                  <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${l.status === "sent" ? "text-green-700" : "text-red-700"}`}>{l.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SettingField({ f, value, placeholder, onChange, configured }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold flex items-center justify-between">
        <span>{f.label}</span>
        {configured && <span className="text-green-700 normal-case tracking-normal text-[10px]">✓ saved</span>}
      </div>
      <input
        type={f.secret ? "password" : "text"}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-zinc-900 focus:outline-none"
        data-testid={`setting-${f.key}`}
      />
    </div>
  );
}
